import { Injectable, inject } from '@angular/core';
import {TenantService} from '../../services/tenant.service';
import {from, Observable, of} from 'rxjs';
import {TeamMember} from '../../models/team-member.model';
import {catchError, concatMap, filter, first, map} from 'rxjs/operators';
import {increment} from '@firebase/firestore';
import {readCollectionWithIds} from '../../common/firestore-utils';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {LoadingService} from '../../shared/services/loading.service';
import {addDoc, collection, collectionData, deleteDoc, doc, docData, Firestore, updateDoc} from '@angular/fire/firestore';
import {Auth, authState, idToken} from '@angular/fire/auth';


@Injectable({
  providedIn:'root'
})
export class EditTeamService {
  private tenant = inject(TenantService);
  private afAuth = inject(Auth);
  private loading = inject(LoadingService);
  private firestore = inject(Firestore);
  private http = inject(HttpClient);

  /**
   * The buyer's purchased seat count, or `null` when it genuinely cannot be read.
   *
   * This feeds a route resolver, and Angular CANCELS a navigation whose resolver errors without
   * surfacing anything - the "Manage team" click just did nothing. `docData` emits `undefined` for
   * a missing `usersPrivate/{uid}` doc, so the old `user.maxTeamSize` threw on that path; a read
   * can also fail offline. Both now emit `null`, which the component renders as an explicit
   * "seat count unavailable" state instead of inventing a number.
   */
  loadTeamSize(): Observable<number | null> {
    return authState(this.afAuth)
      .pipe(
        filter(user => !!user),
        first(),
        concatMap(user => docData(doc(this.firestore, `/schools/${this.tenant.id}/usersPrivate/${user.uid}`), {idField: "id"})),
        map((user:any) => {
          const raw = user?.maxTeamSize;
          // the component used to parseInt() this, so string values stay tolerated
          const size = typeof raw === 'number' ? raw : typeof raw === 'string' ? parseInt(raw, 10) : NaN;
          return Number.isFinite(size) ? size : null;
        }),
        first(),
        catchError(() => of(null))
      )
  }

  /** The team member list, or `null` when the read failed - see `loadTeamSize()` for why it cannot error. */
  loadTeamMembers(): Observable<TeamMember[] | null> {
    return authState(this.afAuth)
      .pipe(
        filter(user => !!user),
        first(),
        concatMap(user => readCollectionWithIds<TeamMember[]>(collectionData(collection(this.firestore,`schools/${this.tenant.id}/usersPrivate/${user.uid}/teamMembers`), {idField: "id"}))),
        first(),
        catchError(() => of(null))
      )
  }

  addTeamMember(teamMember: Partial<TeamMember>): Observable<any> {

    const saveTeamMember$ = authState(this.afAuth)
      .pipe(
        filter(user => !!user),
        first(),
        concatMap(user => from(
           addDoc(collection(this.firestore, `schools/${this.tenant.id}/usersPrivate/${user.uid}/teamMembers`), teamMember)
        )),
        first(),
        map(ref => {
          return {
            id:ref.id,
            ...teamMember
          }
        })
      );

    return this.loading.showLoaderUntilCompleted(saveTeamMember$);
  }

  resendLink(teamMember: TeamMember) {
    const resendLink$ = authState(this.afAuth)
      .pipe(
        filter(user => !!user),
        first(),
        concatMap(user => from(
          updateDoc(doc(this.firestore, `schools/${this.tenant.id}/usersPrivate/${user.uid}/teamMembers/${teamMember.id}`), {linksSentCounter: increment(1)})
        )),
        first()
      );

    return this.loading.showLoaderUntilCompleted(resendLink$);
  }

  removeTeamMember(teamMember: TeamMember) :Observable<any> {

    const removeTeamMember$ = authState(this.afAuth)
      .pipe(
        filter(user => !!user),
        concatMap(user => from(
          deleteDoc(doc(this.firestore, `schools/${this.tenant.id}/usersPrivate/${user.uid}/teamMembers/${teamMember.id}`))
        )),
        first()
      );

    return this.loading.showLoaderUntilCompleted(removeTeamMember$);

  }

  activateTeamMember(managerUserId:string, teamMemberId:string): Observable<any> {
    return idToken(this.afAuth)
      .pipe(
        filter(jwt => !!jwt),
        first(),
        concatMap(() => this.http.post<any>(environment.api.activateTeamMemberUrl, {
          managerUserId,
          teamMemberId,
          tenantId: this.tenant.id
        })),
      );
  }

  updateTeamSize(newTeamSize:number) :Observable<any> {
    return this.http.post(environment.api.updateTeamSizeUrl, {newTeamSize});
  }


}
